import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { REACT_APP_GLOBAL_API_BASE_URL_V2 } from 'runtimeEnvVars';

import {
  fetchApplicantDetailsInit,
  setApplicantDetail,
  updateApplicantTableStatuses,
} from 'containers/ApplicantTableV2/actions';
import { addErrorMessageFromCaughtValueI18n } from 'containers/FlashMessage/actions';
import { apiGet } from 'utils/axios';

import { applicantDetailInFlight as inFlight } from './applicantDetailInFlight';

/**
 * `shared` joins a request already in flight for the applicant. The applicant
 * drawer mounts dozens of components that each call this hook, so without it
 * one page load sent the same GET up to 50 times. A refetch after
 * a change leaves it off, so it never receives a response that predates the
 * change, and later shared callers join the fresh request instead.
 */
export const fetchApplicantData = (applicantId, { shared = false } = {}) => {
  if (shared && inFlight.has(applicantId)) return inFlight.get(applicantId);
  const url = `${REACT_APP_GLOBAL_API_BASE_URL_V2}/applicants/${applicantId}`;
  const request = apiGet(url);
  inFlight.set(applicantId, request);
  const clear = () => {
    if (inFlight.get(applicantId) === request) inFlight.delete(applicantId);
  };
  request.then(clear, clear);
  return request;
};

export default function useApplicantDetail(
  applicantId,
  defaultFetching = true,
  { onSettled, reportUnavailable = false } = {},
) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [data, setData] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState('');
  const [settlement, setSettlement] = useState(null);
  const requestIdRef = useRef(0);
  const loadedApplicantIdRef = useRef(null);
  const currentApplicantIdRef = useRef(applicantId);
  currentApplicantIdRef.current = applicantId;
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;
  const reportUnavailableRef = useRef(reportUnavailable);
  reportUnavailableRef.current = reportUnavailable;

  // Callers may pass an event, so only a literal `{ shared: true }` shares.
  const fetchData = useCallback(
    options => {
      const reportSettled = onSettledRef.current;
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      const isCurrent = () =>
        requestId === requestIdRef.current &&
        currentApplicantIdRef.current === applicantId;
      if (reportUnavailableRef.current) {
        setError('');
        setSettlement(null);
        if (loadedApplicantIdRef.current !== applicantId) {
          setData(null);
          setIsFetching(true);
        }
      }
      let outcome = 'failed';
      dispatch(fetchApplicantDetailsInit({ applicantId }));
      dispatch(
        setApplicantDetail({ info: { id: applicantId }, isFetching: true }),
      );
      fetchApplicantData(applicantId, { shared: options?.shared === true })
        .then(res => {
          if (!isCurrent()) return;
          const { applicant } = res.data;

          setData(applicant);
          loadedApplicantIdRef.current = applicant ? applicantId : null;
          dispatch(updateApplicantTableStatuses(applicantId, applicant.status));
          setError('');
          if (applicant) outcome = 'ok';
        })
        .catch(err => {
          if (!isCurrent()) return;
          if (err?.status === 403 || err?.status === 404)
            outcome = 'unavailable';
          if (
            reportUnavailableRef.current &&
            loadedApplicantIdRef.current !== applicantId
          ) {
            loadedApplicantIdRef.current = null;
            setData(null);
          }
          setError(
            reportUnavailableRef.current && outcome === 'unavailable'
              ? ''
              : err?.message || err,
          );
        })
        .finally(() => {
          if (!isCurrent()) return;
          setSettlement({ applicantId, outcome });
          setIsFetching(false);
          reportSettled?.(outcome);
        });
    },
    [applicantId, dispatch],
  );

  // The interceptor rejects with the response body's message, so a 403 names
  // the denial instead of the generic error.
  useEffect(() => {
    if (error) {
      dispatch(addErrorMessageFromCaughtValueI18n(error, intl));
    }
  }, [dispatch, error, intl]);

  useEffect(() => {
    if (applicantId && defaultFetching) {
      fetchData({ shared: true });
    }
    return () => {
      requestIdRef.current += 1;
    };
  }, [applicantId, fetchData, defaultFetching]);

  const currentOutcome =
    reportUnavailable &&
    applicantId &&
    loadedApplicantIdRef.current !== applicantId &&
    settlement?.applicantId === applicantId
      ? settlement.outcome
      : null;
  return {
    data,
    error,
    isFetching,
    fetchData,
    setIsFetching,
    unavailable: currentOutcome === 'unavailable',
    failed: currentOutcome === 'failed',
  };
}
